import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, Check, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Cache of known genres for autocomplete. Populated once, then patched
// optimistically as the user creates new ones inline.
let _genreCache = null;
// Deduplicates concurrent calls — 60+ <GenreEditor/> instances mount at
// once on the Library page (one per track row); without this, every one
// of them would fire its own axios.get('/api/genres') before the first
// response lands (a ~60-request storm). With this, only one request goes
// out and all callers await the same promise.
let _genreLoadPromise = null;
const cacheListeners = new Set();

async function loadGenres() {
  if (_genreCache) return _genreCache;
  if (_genreLoadPromise) return _genreLoadPromise;
  _genreLoadPromise = (async () => {
    try {
      const { data } = await axios.get(`${API}/genres`).catch(() => ({ data: null }));
      if (data?.genres) {
        _genreCache = [...data.genres].sort();
      } else {
        // Fallback: pull from /tracks and dedupe locally
        const { data: t } = await axios.get(`${API}/tracks`);
        const set = new Set();
        (t?.tracks || []).forEach((tr) => tr.genre && set.add(tr.genre));
        _genreCache = Array.from(set).sort();
      }
    } catch {
      _genreCache = [];
    }
    cacheListeners.forEach((fn) => fn(_genreCache));
    return _genreCache;
  })();
  return _genreLoadPromise;
}

// Exported so other components (LibraryPage's genre filter dropdown) can
// share the same de-duplicated cache instead of firing their own request.
export { loadGenres };

function pushGenre(g) {
  if (!g || !_genreCache) return;
  if (!_genreCache.includes(g)) {
    _genreCache = [..._genreCache, g].sort();
    cacheListeners.forEach((fn) => fn(_genreCache));
  }
}

/**
 * Inline genre editor — shows as a chip; click to edit; saves via PATCH /api/tracks/{id}.
 * Variants:
 *   - "chip"  → small badge style for table rows (Playlist / Set / Crate)
 *   - "card"  → mono text for the Library track card footer
 */
export default function GenreEditor({
  track,
  variant = 'chip',
  onChange,
  className = '',
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(track?.genre || '');
  const [saving, setSaving] = useState(false);
  const [genres, setGenres] = useState(_genreCache || []);
  const inputRef = useRef(null);
  const datalistId = `genre-list-${track?.id || 'x'}`;

  useEffect(() => { setValue(track?.genre || ''); }, [track?.genre]);

  useEffect(() => {
    let mounted = true;
    if (!_genreCache) loadGenres().then((g) => mounted && setGenres(g));
    const fn = (g) => mounted && setGenres(g);
    cacheListeners.add(fn);
    return () => { mounted = false; cacheListeners.delete(fn); };
  }, []);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = useCallback(async () => {
    const next = value.trim();
    if (next === (track?.genre || '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/tracks/${track.id}`, { genre: next || null });
      pushGenre(next);
      onChange?.(data?.track || { ...track, genre: next });
      toast.success(next ? `Genre set to "${next}"` : 'Genre cleared');
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save genre');
    } finally {
      setSaving(false);
    }
  }, [value, track, onChange]);

  const cancel = useCallback(() => {
    setValue(track?.genre || '');
    setEditing(false);
  }, [track]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  if (editing) {
    return (
      <span
        onClick={stop}
        className={`inline-flex items-center gap-1 ${className}`}
        data-testid={`genre-editor-${track.id}`}
      >
        <input
          ref={inputRef}
          list={datalistId}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => setTimeout(save, 120)}
          disabled={saving}
          placeholder="Genre…"
          className="px-2 py-0.5 rounded-md border border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/20 outline-none text-xs font-mono w-32"
          data-testid={`genre-input-${track.id}`}
        />
        <datalist id={datalistId}>
          {genres.map((g) => <option key={g} value={g} />)}
        </datalist>
        <button
          onMouseDown={stop}
          onClick={save}
          disabled={saving}
          className="w-5 h-5 rounded-md bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 disabled:opacity-50"
          data-testid={`genre-save-${track.id}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </button>
        <button
          onMouseDown={stop}
          onClick={cancel}
          disabled={saving}
          className="w-5 h-5 rounded-md border border-[#E2E8F0] text-[#64748B] flex items-center justify-center hover:bg-[#F1F5F9]"
          data-testid={`genre-cancel-${track.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  const placeholder = '+ genre';
  const hasGenre = !!track?.genre;
  const display = hasGenre ? track.genre : placeholder;

  if (variant === 'card') {
    return (
      <button
        onClick={(e) => { stop(e); setEditing(true); }}
        className={`font-mono text-xs ${hasGenre ? 'text-[#94A3B8] hover:text-[#2563FF]' : 'text-[#CBD5E1] hover:text-[#2563FF]'} transition-colors ${className}`}
        data-testid={`genre-cell-${track.id}`}
        title="Click to edit genre"
      >
        {display}
      </button>
    );
  }

  // chip variant (default)
  return (
    <button
      onClick={(e) => { stop(e); setEditing(true); }}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-mono transition-all ${
        hasGenre
          ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/15 hover:bg-[#DBEAFE]'
          : 'text-[#94A3B8] border border-dashed border-[#E2E8F0] hover:border-[#2563FF] hover:text-[#2563FF]'
      } ${className}`}
      data-testid={`genre-cell-${track.id}`}
      title="Click to edit genre"
    >
      <Tag className="w-3 h-3" />
      <span className="max-w-[120px] truncate">{display}</span>
    </button>
  );
}
